import { SpaceWeatherWidgetCard } from './useDashboardWidgets';
import { useSpaceWeatherCycles } from '@/hooks/useSpaceWeatherCycles';
import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useMemo,
  useContext,
  useLayoutEffect,
  startTransition,
} from "react";

import {
  resolveDashboardLayoutStorageKey,
  useDashboardBreakpoint,
} from "@/hooks/useDashboardBreakpoint";

import {
  sanitizeDashboardViews as normalizeDashboardViews,
  subscribeDashboardMeta,
  loadDashboardMeta,
} from "@/lib/dashboardStore";
import {
  subscribeDashboardLayout,
  loadDashboardLayout,
  saveDashboardMeta,
  deleteDashboardLayout,
} from "@/lib/dashboardStore";
import { usePaulAccess } from '@/lib/paulAccess';
import { useCalendar13 } from "@/hooks/useCalendar13";
import type {
  DashboardView,
  DashboardLayoutState,
  WidgetCustomizationState,
} from "@/lib/dashboardTypes";
import type { RingThemeState } from "@/lib/ringThemeStore";
import {
  loadTimelineData,
  saveTimelineData,
  subscribeTimelineData,
  allowTimelineSaveOnce,
  TimelineData,
} from "@/lib/timelineStore";
import {
  loadCycleData,
  saveCycleData,
  subscribeCycleData,
  CycleData,
} from "@/lib/cycleStore";
import {
  getRelationshipDocId,
  saveRelationshipData,
} from "@/lib/relationshipStore";
import { gregorianToDay13, day13ToGregorian, Day13, getYearStartDate } from "@/lib/calendar13";
import { subMonths } from "date-fns/subMonths";
import { addMonths } from "date-fns/addMonths";
import { useDashboardCycles } from "./useDashboardCycles";
import { useDashboardWidgets } from "./useDashboardWidgets";
import { signOut as appSignOut } from '@/lib/auth';
// Icons are not found in src/components/icons, so comment out these imports for now
// import { Grid3X3, Navigation, Shield } from '@/components/icons';

// Default max widget rings for dashboard layouts
const MAX_WIDGET_RINGS = 20;
const CORE_CYCLE_IDS = new Set(['days', 'moons', 'lunar']);
const CORE_CYCLE_ORDER: Record<string, number> = {
  lunar: 0,  // outermost
  days: 1,   // middle
  moons: 2,  // innermost
};

// WidgetSyncMode is not exported from dashboardStore, so define locally
type WidgetSyncMode = "linked" | "isolated" | "fixed";

type MasterSyncState = { widgetId: string; timestamp?: string };

const serializeTimelineState = (data: { mode: string; timestamp?: string | null | undefined }): string =>
  `${data.mode}:${data.timestamp ?? ''}`;
import {
  loadRingThemeState,
  subscribeRingThemeState,
  saveRingThemeState,
} from "@/lib/ringThemeStore";
import { toast } from "@/hooks/use-toast";
import type { CycleRing } from "@/lib/cycleTypes";
import { DashboardPageView } from "./DashboardPageView";
import {
  loadSelectedGroupId,
  subscribeSelectedGroup,
} from "@/lib/cycleGroupStore";
import {
  getDefaultCycleSet,
  CycleAlignment,
  createCycleFromPreset,
  getLeapCyclePosition,
  SOLAR_SYSTEM_GROUP,
  CANONICAL_RING_DISPLAY_ORDER,
} from "@/lib/cycleConfig";

const DashboardPage = ({ initialViewId }: { initialViewId?: string }) => {
  const [widgetCustomizations, setWidgetCustomizations] = useState<
    Record<string, WidgetCustomizationState>
  >({});
  const [requiresBetaAccess, setRequiresBetaAccess] = useState(false);
  const [showCycleWizard, setShowCycleWizard] = useState(false);
  const { isPaul, email } = usePaulAccess();
  const isLoggedIn = Boolean(email);
  const isAdmin = isPaul;
  const canEditDashboard = isPaul;
  const [showAlignmentTool, setShowAlignmentTool] = useState(false);
  const [showRingThemeWizard, setShowRingThemeWizard] = useState(false);
  const relationshipAutosaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastRelationshipAutosavePayloadRef = useRef<string | null>(null);
  const [alignmentInitialCycleId, setAlignmentInitialCycleId] = useState<
    string | undefined
  >();
  const [hiddenRings, setHiddenRings] = useState<string[]>(['moons']);
  const [widgetVisibleCount, setWidgetVisibleCount] = useState<
    number | undefined
  >(undefined);
  const [widgetSyncState, setWidgetSyncState] = useState<
    "all-linked" | "all-unlinked" | "partial"
  >("all-linked");
  const [dashboardViews, setDashboardViews] = useState<DashboardView[]>([]);
  const [activeViewId, setActiveViewIdState] = useState("");
  const initialViewAppliedRef = useRef(false);
  
  // Local-only setter. Persist view selection only from explicit user actions.
  const setActiveViewId = useCallback((newViewId: string) => {
    activeViewIdRef.current = newViewId;
    setActiveViewIdState(newViewId);
  }, []);

  useEffect(() => {
    if (!initialViewId || initialViewAppliedRef.current) return;
    if (!dashboardViews.length) return;
    const match = dashboardViews.find((view) => view.id === initialViewId);
    if (!match) return;
    initialViewAppliedRef.current = true;
    setActiveViewId(match.id);
  }, [dashboardViews, initialViewId, setActiveViewId]);
  
  const [visibleWidgets, setVisibleWidgets] = useState<string[]>([]);
  const [widgetSyncModes, setWidgetSyncModes] = useState<
    Record<string, WidgetSyncMode>
  >({});
  const [widgetEditModes, setWidgetEditModes] = useState<
    Record<string, boolean>
  >({});
  const [isolatedWidgetValues, setIsolatedWidgetValues] = useState<
    Record<string, number>
  >({});
  const [lastMasterWidgetId, setLastMasterWidgetId] = useState<string | null>(null);
  const [betaEnabled, setBetaEnabled] = useState(false);
  const [betaDashboardsVisible, setBetaDashboardsVisible] = useState(false);
  const [metaLoaded, setMetaLoaded] = useState(false);
  const [ringThemeState, setRingThemeState] = useState<RingThemeState>({
    themes: [],
  });
  const hasSeededWidgetsRef = useRef<string | null>(null);
  const dashboardBreakpoint = useDashboardBreakpoint();
  const [firestoreDisabled, setFirestoreDisabled] = useState<boolean>(false);
  const [firestoreError, setFirestoreError] = useState<boolean>(false);

  const handleSignOut = useCallback(async () => {
    try {
      // Save current widget customizations (cyclerow selector, etc) globally before signing out
      if (Object.keys(widgetCustomizations).length > 0) {
        try {
          const { saveGlobalWidgetCustomizations } = await import('@/lib/dashboardStore');
          await saveGlobalWidgetCustomizations(widgetCustomizations);
          console.log('[DashboardPageImpl] Widget customizations saved globally for logged-out users');
        } catch (err) {
          console.error('[DashboardPageImpl] Failed to save global widget customizations:', err);
        }
      }
      await appSignOut();
      window.location.assign('/');
    } catch (error) {
      console.error('[DashboardPageImpl] Sign out failed:', error);
      toast({
        title: 'Sign out failed',
        description: 'Please try again.',
        variant: 'destructive',
      });
    }
  }, [widgetCustomizations]);

  // Track optimistic updates to prevent stale Firestore data from reverting them
  const lastOptimisticUpdateRef = useRef<{ timestamp: number; views: DashboardView[] } | null>(null);
  const metaSaveQueueRef = useRef<Promise<void>>(Promise.resolve());
  const dashboardViewsRef = useRef<DashboardView[]>([]);
  const activeViewIdRef = useRef<string>('');
  const metaLoadedRef = useRef(false);

  dashboardViewsRef.current = dashboardViews;
  activeViewIdRef.current = activeViewId;
  metaLoadedRef.current = metaLoaded;

  // All dashboardStore functions are now statically imported

  const maxWidgetRings = useMemo(() => {
    if (dashboardBreakpoint === "mobile") return 5;
    if (dashboardBreakpoint === "tablet") return 10;
    return MAX_WIDGET_RINGS;
  }, [dashboardBreakpoint]);

  const persistDashboardViews = useCallback(
    (views: DashboardView[], nextActiveViewId?: string) => {
      const job = async () => {
        // Everyone reads shared dashboard state; only Paul can mutate it.
        // Non-Paul users: changes fail silently via Firestore permission rules.
        await saveDashboardMeta({ views, activeViewId: nextActiveViewId });
      };

      const queued = metaSaveQueueRef.current.catch(() => undefined).then(job);
      metaSaveQueueRef.current = queued.catch(() => undefined);
      return queued;
    },
    [],
  );

  const handleStorageChange = useCallback(() => {
    let unsubscribe: (() => void) | undefined;
    let isActive = true;
    // Helper to apply dashboard meta
    function applyMeta(meta: any) {
      // Type assertion for meta
      const typedMeta = meta as { views: DashboardView[]; activeViewId?: string };
      console.log('[DashboardPageImpl] applyMeta called:', { hasViews: !!typedMeta?.views, viewCount: typedMeta?.views?.length, activeViewId: typedMeta?.activeViewId });
      if (typedMeta && Array.isArray(typedMeta.views)) {
        // Check if we have a recent optimistic update that should take precedence
        const optimisticUpdate = lastOptimisticUpdateRef.current;
        if (optimisticUpdate && Date.now() - optimisticUpdate.timestamp < 5000) {
          // Use optimistic update if it's less than 5 seconds old
          console.log('[applyMeta] Using optimistic update instead of Firestore data');
          setDashboardViews(optimisticUpdate.views);
          // Clear the optimistic update after using it
          lastOptimisticUpdateRef.current = null;
        } else {
          // Use Firestore data
          console.log('[applyMeta] Using Firestore data:', { viewCount: typedMeta.views.length });
          setDashboardViews(typedMeta.views);
        }
        if (typedMeta.activeViewId && typedMeta.views.some(v => v.id === typedMeta.activeViewId)) {
          console.log('[applyMeta] Setting activeViewId from Firestore:', typedMeta.activeViewId);
          setActiveViewId(typedMeta.activeViewId);
        } else if (typedMeta.views.length > 0) {
          console.log('[applyMeta] No activeViewId, defaulting to first view:', typedMeta.views[0].id);
          setActiveViewId(typedMeta.views[0].id);
        }
      }
    }
    setFirestoreDisabled(false);
    (async () => {
      // Everyone reads from shared dashboard state.
      const subscriptionId = Math.random().toString(36).substring(7);
      console.log('[DashboardPageImpl] Setting up subscription with ID:', subscriptionId);
      try {
        unsubscribe = subscribeDashboardMeta((meta) => {
          console.log('[DashboardPageImpl] subscribeDashboardMeta callback fired with meta:', subscriptionId, meta);
          applyMeta(meta);
          if (isActive) setMetaLoaded(true);
          if (isActive) setFirestoreError(false);
        });
        console.log('[DashboardPageImpl] Subscription setup complete, unsubscribe:', subscriptionId, !!unsubscribe);
      } catch (err) {
        console.error('[DashboardPageImpl] Subscription error:', subscriptionId, err);
      }
      if (!unsubscribe) {
        loadDashboardMeta()
          .then((data) => {
            applyMeta(data);
            if (isActive) setMetaLoaded(true);
            if (isActive) setFirestoreError(false);
          })
          .catch((err) => {
            console.error('[DashboardPageImpl] Initial dashboard meta load failed:', subscriptionId, err);
            if (isActive) setFirestoreError(true);
          });
      }
    })();

    return () => {
      isActive = false;
      unsubscribe?.();
      if (typeof window !== "undefined") {
        window.removeEventListener("storage", handleStorageChange);
        window.removeEventListener(
          "developer-unlocked-changed",
          handleStorageChange as EventListener,
        );
      }
    };
  }, [isAdmin]);

  // Call handleStorageChange immediately on mount to setup subscription
  useEffect(() => {
    handleStorageChange();
  }, [handleStorageChange]);

  const safeDashboardViews = useMemo(
    () => normalizeDashboardViews(dashboardViews),
    [dashboardViews],
  );

  const activeView = useMemo(() => {
    if (!safeDashboardViews.length) return null;
    const view =
      safeDashboardViews.find((view) => view.id === activeViewId) ??
      safeDashboardViews[0];
    return view;
  }, [activeViewId, safeDashboardViews]);

  const ensureBaselineWidgets = useCallback((widgetIds: string[]) => {
    const needed = ["gregorian-grid", "grid", "waveform", "reminders", "ai-day-summary", "year-ring"];
    const existing = new Set(widgetIds);
    const missing = needed.filter((id) => !existing.has(id));
    return missing.length ? [...missing, ...widgetIds] : widgetIds;
  }, []);

  useEffect(() => {
    if (!metaLoaded) return;
    if (safeDashboardViews.length > 0) return;
    if (hasSeededWidgetsRef.current === "default-view") return;

    const fallbackWidgets = visibleWidgets.length > 0 ? visibleWidgets : ["gregorian-grid", "timeline"];
    const widgetIds = ensureBaselineWidgets(fallbackWidgets);
    const defaultView: DashboardView = {
      id: "dashboard",
      name: "Dashboard",
      widgetIds,
    };

    // Important: do not auto-persist bootstrapped defaults, because a transient
    // load race can overwrite existing Firestore settings. Persist only from
    // explicit user actions (create/add/remove/toggle).
    hasSeededWidgetsRef.current = "default-view";
    setDashboardViews([defaultView]);
    dashboardViewsRef.current = [defaultView];
    setActiveViewId(defaultView.id);
    activeViewIdRef.current = defaultView.id;
  }, [
    ensureBaselineWidgets,
    metaLoaded,
    safeDashboardViews.length,
    setActiveViewId,
    visibleWidgets,
  ]);

  useLayoutEffect(() => {
    if (!activeView) {
      setVisibleWidgets(["timeline"]);
      setWidgetSyncModes({});
      setWidgetEditModes({});
      setRingThemeState({ themes: [] });
      return;
    }

    // Always force agenda and reminders to be visible
    let nextVisible = ensureBaselineWidgets(activeView.widgetIds);
    const forced = ["agenda", "reminders"];
    for (const id of forced) {
      if (!nextVisible.includes(id)) {
        nextVisible = [id, ...nextVisible];
      }
    }
    setVisibleWidgets(nextVisible);
  }, [activeView, ensureBaselineWidgets]);

  useEffect(() => {
    if (!activeView) return;
    setRingThemeState(loadRingThemeState(activeView.id));
    const unsubscribe = subscribeRingThemeState(
      activeView.id,
      setRingThemeState,
    );
    return () => unsubscribe?.();
  }, [activeView]);

  const updateRingThemeState = useCallback(
    (updater: (prev: RingThemeState) => RingThemeState) => {
      setRingThemeState((prev) => {
        const next = updater(prev);
        if (activeView) {
          saveRingThemeState(activeView.id, next);
        }
        return next;
      });
    },
    [activeView],
  );

  useEffect(() => {
    if (!dashboardViews.length) return;
    if (!dashboardViews.some((view) => view.id === activeViewId)) {
      setActiveViewId(dashboardViews[0].id);
    }
  }, [activeViewId, dashboardViews]);

  useEffect(() => {
    if (!activeView) return;
    let isActive = true;
    const baseStorageKey = `galactic-dashboard-layout-${activeView.id}`;
    const storageKey = resolveDashboardLayoutStorageKey(
      baseStorageKey,
      dashboardBreakpoint,
    );

    const applyLayout = (layout: DashboardLayoutState | null) => {
      if (!isActive) return;
      const customizations = layout?.customizations ?? {};
      setWidgetCustomizations(customizations); // <-- pass full customizations to skin view
      const nextSyncModes = Object.fromEntries(
        Object.entries(customizations).map(([widgetId, customization]) => [
          widgetId,
          customization.syncMode ?? "linked",
        ]),
      ) as Record<string, WidgetSyncMode>;
      setWidgetSyncModes(nextSyncModes);
    };

    let unsubscribe: (() => void) | undefined;

    (async () => {
      try {
        unsubscribe = subscribeDashboardLayout(storageKey, applyLayout);
      } catch {
        // noop: fall through to load
      }

      if (!unsubscribe) {
        try {
          const layout = await loadDashboardLayout(storageKey);
          applyLayout(layout);
        } catch {
          // ignore
        }
      }
    })();

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, [activeView, dashboardBreakpoint]);

  const areWidgetIdsEqual = useCallback((first: string[], second: string[]) => {
    if (first.length !== second.length) return false;
    return first.every((value, index) => value === second[index]);
  }, []);

  const updateActiveViewWidgets = useCallback(
    async (nextWidgetIds: string[]) => {
      const currentViews = dashboardViewsRef.current;
      const currentActiveViewId = activeViewIdRef.current;
      const hasLoadedMeta = metaLoadedRef.current;

      if (!hasLoadedMeta && currentViews.length === 0) return;

      const resolvedActiveViewId = currentViews.some((view) => view.id === currentActiveViewId)
        ? currentActiveViewId
        : currentViews[0]?.id ?? '';

      const activeIndex = currentViews.findIndex(
        (view) => view.id === resolvedActiveViewId,
      );
      if (activeIndex === -1) return;
      const activeView = currentViews[activeIndex];
      if (areWidgetIdsEqual(activeView.widgetIds, nextWidgetIds)) {
        return;
      }

      const nextViews = [...currentViews];
      nextViews[activeIndex] = { ...activeView, widgetIds: [...nextWidgetIds] };

      // Save to Firebase first, then update local state
      try {
        await persistDashboardViews(nextViews, resolvedActiveViewId || undefined);
        // Record optimistic update timestamp to prevent stale Firestore data from reverting
        lastOptimisticUpdateRef.current = { timestamp: Date.now(), views: nextViews };
        setDashboardViews(nextViews);
      } catch (error) {
        console.error("Failed to save dashboard meta:", error);
        toast({
          title: "Failed to save",
          description: "Could not update dashboard configuration",
          variant: "destructive",
        });
      }
    },
    [areWidgetIdsEqual, persistDashboardViews],
  );

  const handleCreateView = useCallback(() => {
    const name = window.prompt("Name this dashboard view");
    if (!name) return;

    // Generate base ID from name
    let baseId =
      name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "") || "view";

    // Ensure ID is unique by appending timestamp if it already exists
    let id = baseId;
    if (safeDashboardViews.some((view) => view.id === id)) {
      id = `${baseId}-${Date.now()}`;
    }

    const newView: DashboardView = {
      id,
      name: name || "Dashboard",
      widgetIds: ensureBaselineWidgets(visibleWidgets),
    };

    const nextViews = [...safeDashboardViews, newView];
    setDashboardViews(nextViews);
    setActiveViewId(id);
    setMetaLoaded(true);
    dashboardViewsRef.current = nextViews;
    activeViewIdRef.current = id;
    metaLoadedRef.current = true;
    (async () => {
      try {
        await persistDashboardViews(nextViews, id);
      } catch {}
    })();
    toast.success("New dashboard view created");
  }, [dashboardViews, persistDashboardViews, visibleWidgets]);

  const handleDeleteView = useCallback(() => {
    if (!activeView) return;
    const confirmed = window.confirm(
      `Delete "${activeView?.name ?? "Dashboard"}"? This cannot be undone.`,
    );
    if (!confirmed) return;
    const nextViews = safeDashboardViews.filter(
      (view) => view && view.id !== activeView.id,
    );
    const nextActiveViewId = nextViews[0]?.id ?? "";
    setDashboardViews(nextViews);
    setActiveViewId(nextActiveViewId);
    (async () => {
      try {
        await persistDashboardViews(nextViews, nextActiveViewId || undefined);
      } catch {}
    })();
    const baseKey = `galactic-dashboard-layout-${activeView.id}`;
    (["mobile", "tablet", "desktop"] as const).forEach((bp) => {
      void deleteDashboardLayout(resolveDashboardLayoutStorageKey(baseKey, bp));
    });
    toast.success("Dashboard view deleted");
  }, [activeView, dashboardViews, isAdmin]);

  const handleSelectView = useCallback(
    (viewId: string) => {
      // Validate the viewId exists in dashboardViews
      if (!safeDashboardViews.some((view) => view.id === viewId)) {
        console.warn(
          `[Dashboard] View ID "${viewId}" not found in views, ignoring selection`,
        );
        return;
      }

      // Update local state immediately
      setActiveViewId(viewId);

      // Save to Firestore immediately to persist selection
      (async () => {
        try {
          await persistDashboardViews(safeDashboardViews, viewId);
        } catch (err) {
          console.error('[Dashboard] Failed to save view selection:', err);
        }
      })();
    },
    [persistDashboardViews, safeDashboardViews],
  );

  const {
    config,
    updateConfig,
    selectedYear,
    setSelectedYear,
    selectedMonth,
    setSelectedMonth,
    selectedDay,
    selectDay,
    today,
    currentMonth,
    allMonths,
    nextMonth,
    prevMonth,
  } = useCalendar13();

  const [currentTime, setCurrentTime] = useState(() => new Date());
  const [displayTime, setDisplayTime] = useState<Date | null>(null);
  const [isManualMode, setIsManualMode] = useState(false);
  const [isNowLocked, setIsNowLocked] = useState(true);
  const [gregorianViewMonth, setGregorianViewMonth] = useState(
    () => new Date(),
  );
  const [autoSync, setAutoSync] = useState(true);
  const [isYearRingSynced, setIsYearRingSynced] = useState(false);
  const handleToggleYearRingSync = useCallback(() => setIsYearRingSynced(prev => !prev), []);
  const serializeCycleRings = useCallback(
    (rings: CycleRing[], includeRuntimeFields: boolean) =>
      rings.map((ring) => ({
        id: ring.id,
        name: ring.name ?? "Unnamed",
        ...(ring.description !== undefined ? { description: ring.description } : {}),
        ...(Array.isArray(ring.stageIconImageUrls)
          ? { stageIconImageUrls: ring.stageIconImageUrls }
          : {}),
        stages: ring.stages,
        labels: ring.labels,
        colors: ring.colors,
        ...(includeRuntimeFields
          ? { currentStage: ring.currentStage, rotation: ring.rotation }
          : {}),
        ...(ring.parentId !== undefined ? { parentId: ring.parentId } : {}),
        ...(ring.stagesPerParent !== undefined
          ? { stagesPerParent: ring.stagesPerParent }
          : {}),
        ...(ring.displayOrder !== undefined
          ? { displayOrder: ring.displayOrder }
          : {}),
        ...(ring.skinMode !== undefined ? { skinMode: ring.skinMode } : {}),
        ...(ring.themeId !== undefined ? { themeId: ring.themeId } : {}),
        ...(ring.waveform !== undefined ? { waveform: ring.waveform } : {}),
        ...(ring.waveformIntervalMultiplier !== undefined
          ? { waveformIntervalMultiplier: ring.waveformIntervalMultiplier }
          : {}),
        ...(ring.waveformIntervalDivider !== undefined
          ? { waveformIntervalDivider: ring.waveformIntervalDivider }
          : {}),
        ...(ring.useCurrentStageAsValue !== undefined
          ? { useCurrentStageAsValue: ring.useCurrentStageAsValue }
          : {}),
      })),
    [],
  );

  const serializeCyclePayload = useCallback(
    (
      rings: CycleRing[],
      hiddenRingIds: string[],
      includeRuntimeFields: boolean,
    ) =>
      JSON.stringify({
        rings: serializeCycleRings(rings, includeRuntimeFields),
        hiddenRings: hiddenRingIds,
      }),
    [serializeCycleRings],
  );

  useEffect(() => {
    const timer = setInterval(() => {
      const now = new Date();
      setCurrentTime(now);
    }, 1000);
    return () => clearInterval(timer);
  }, []);

  // In manual mode, keep timeline anchored to the selected timestamp.
  // Advancing this clock every second causes visible cycle jitter while editing.

  const dayOfYear = useMemo(() => {
    // Day of year is 1-indexed: Day 1 of Month 1 = 1, Drift = 365
    return today.isOutOfTime ? 365 : (today.month - 1) * 28 + today.day;
  }, [today]);

  const shownTime = displayTime || currentTime;

  const [cycleRings, setCycleRings] = useState<CycleRing[]>(() => {
    return getDefaultCycleSet(dayOfYear, new Date()).cycles;
  });
  const coreCycleRings = useMemo(
    () =>
      cycleRings
        .filter((ring) => CORE_CYCLE_IDS.has(ring.id))
        .map((ring) => ({ ...ring, displayOrder: CORE_CYCLE_ORDER[ring.id] ?? ring.displayOrder }))
        .sort((a, b) => (a.displayOrder ?? 999) - (b.displayOrder ?? 999)),
    [cycleRings],
  );
  const forcedHiddenRings = useMemo(
    () => cycleRings.filter((ring) => !CORE_CYCLE_IDS.has(ring.id)).map((ring) => ring.id),
    [cycleRings],
  );
  const effectiveHiddenRings = useMemo(
    () => Array.from(new Set([...hiddenRings, ...forcedHiddenRings])),
    [hiddenRings, forcedHiddenRings],
  );
  const maxWidgetRingsWithCycles = useMemo(
    () => Math.max(maxWidgetRings, cycleRings.length),
    [maxWidgetRings, cycleRings.length],
  );

  const viewId = activeView?.id ?? null;
  const [alignments, setAlignments] = useState<CycleAlignment[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(() =>
    loadSelectedGroupId(viewId),
  );
  const cycleDocId = useMemo(() => {
    const docId = activeView ? `${activeView.id}-${selectedGroupId}` : null;
    return docId;
  }, [activeView, selectedGroupId]);
  const relationshipDocId = useMemo(() => {
    if (!viewId) return null;
    const scopedViewId = selectedGroupId ? `${viewId}-${selectedGroupId}` : viewId;
    return getRelationshipDocId(scopedViewId);
  }, [selectedGroupId, viewId]);
  const timelineDocId = useMemo(
    () => (activeView ? `${activeView.id}-timeline` : null),
    [activeView],
  );
  const masterSyncSessionKey = useMemo(
    () => (timelineDocId ? `master-sync:${timelineDocId}` : null),
    [timelineDocId],
  );
  const [cyclesLoaded, setCyclesLoaded] = useState(false);
  const [timelineLoaded, setTimelineLoaded] = useState(false);
  const lastSavedCyclesRef = useRef<string | null>(null);
  const lastSavedTimelineRef = useRef<string | null>(null);
  const displayTimeRef = useRef<Date | null>(null);
  const selectedGroupIdRef = useRef<string | null>(loadSelectedGroupId(viewId));
  const cycleRingsRef = useRef<CycleRing[]>(cycleRings);
  const hiddenRingsRef = useRef<string[]>(hiddenRings);
  const widgetVisibleCountRef = useRef<number | undefined>(widgetVisibleCount);
  const lastTimelineLocalUpdateRef = useRef<number>(0);
  const pendingTimelineRef = useRef<string | null>(null);
  const timelineDirtyRef = useRef(false);
  const firestoreWriteTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingFirestorePayloadRef = useRef<TimelineData | null>(null);
  const lastMasterTimestampRef = useRef<string | null>(null);
  const [pendingTimelineVersion, setPendingTimelineVersion] = useState(0);

  const persistMasterSyncState = useCallback(
    (widgetId: string, timestamp?: string) => {
      if (!widgetId) return;
      setLastMasterWidgetId(widgetId);
      if (timestamp) {
        lastMasterTimestampRef.current = timestamp;
      }
      if (!masterSyncSessionKey) return;
      try {
        const payload: MasterSyncState = {
          widgetId,
          ...(timestamp ? { timestamp } : lastMasterTimestampRef.current ? { timestamp: lastMasterTimestampRef.current } : {}),
        };
        sessionStorage.setItem(masterSyncSessionKey, JSON.stringify(payload));
      } catch {
        // Ignore storage errors in private/restricted contexts.
      }
    },
    [masterSyncSessionKey],
  );

  useEffect(() => {
    setLastMasterWidgetId(null);
    lastMasterTimestampRef.current = null;
    if (!masterSyncSessionKey) return;
    try {
      const raw = sessionStorage.getItem(masterSyncSessionKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as MasterSyncState;
      if (typeof parsed.widgetId === "string" && parsed.widgetId.length > 0) {
        setLastMasterWidgetId(parsed.widgetId);
      }
      if (typeof parsed.timestamp === "string" && parsed.timestamp.length > 0) {
        lastMasterTimestampRef.current = parsed.timestamp;
      }
    } catch {
      // Ignore malformed session payloads.
    }
  }, [masterSyncSessionKey]);

  useEffect(() => {
    const nextSelected = loadSelectedGroupId(viewId);
    setSelectedGroupId(nextSelected);
    selectedGroupIdRef.current = nextSelected;
  }, [viewId]);

  const setPendingTimeline = useCallback((data: TimelineData) => {
    if (data.mode === "live") return;
    pendingTimelineRef.current = serializeTimelineState(data);
    lastTimelineLocalUpdateRef.current = Date.now();
    timelineDirtyRef.current = true;
    setPendingTimelineVersion((v) => v + 1);
  }, []);

  useEffect(() => {
    cycleRingsRef.current = cycleRings;
  }, [cycleRings]);

  useEffect(() => {
    hiddenRingsRef.current = effectiveHiddenRings;
  }, [effectiveHiddenRings]);

  useEffect(() => {
    widgetVisibleCountRef.current = widgetVisibleCount;
  }, [widgetVisibleCount]);

  useEffect(() => {
    displayTimeRef.current = displayTime;
  }, [displayTime]);

  // Alignments are now derived from alignmentTool interactions only
  // Interlocking cycles use per-ring parentId and stagesPerParent stored in cycleStore

  const saveCyclesNow = useCallback(
    async (nextRings: CycleRing[], nextHiddenRings: string[]) => {
      // Persist cycles to Firestore only.
      // Deduplicate using a payload that omits runtime fields (rotation/currentStage)
      if (!cyclesLoaded || !cycleDocId) return;

      const serializedStable = serializeCyclePayload(
        nextRings,
        nextHiddenRings,
        false,
      );
      if (lastSavedCyclesRef.current === serializedStable) return;

      lastSavedCyclesRef.current = serializedStable;
      const payload = { rings: nextRings, hiddenRings: nextHiddenRings };

      try {
        await saveCycleData(cycleDocId, payload);
      } catch (err) {
        console.error("cycle save error", err);
      }
    },
    [cycleDocId, cyclesLoaded, serializeCyclePayload],
  );

  // When a cycle group is selected (from any widget), load its rings and apply them globally
  useEffect(() => {
    let active = true;
    const unsub = subscribeSelectedGroup(viewId, async (groupId) => {
      if (!active) return;
      const nextGroupId = groupId || null;
      selectedGroupIdRef.current = nextGroupId;
      setSelectedGroupId(nextGroupId);
    });
    return () => {
      active = false;
      unsub?.();
    };
  }, [viewId]);

  useEffect(() => {
    if (!activeView || !cycleDocId) return;
    let isActive = true;
    setCyclesLoaded(false);
    if (!selectedGroupId) {
      setHiddenRings(['moons']);
    }
    lastSavedCyclesRef.current = null;
    if (!selectedGroupId) {
      setCycleRings(getDefaultCycleSet(dayOfYear, new Date()).cycles);
    }

    const applyCycleData = (data: CycleData | null) => {
      if (!isActive) return;
      if (!data || !data.rings.length) {
        lastSavedCyclesRef.current = serializeCyclePayload([], ['moons'], false);
        // Load defaults if no data exists
        setCycleRings(getDefaultCycleSet(dayOfYear, new Date()).cycles);
        setHiddenRings(['moons']);
        return;
      }
      // Filter out any undefined/null rings before applying
      const validRings = data.rings.filter((ring) => ring && ring.id);
      if (!validRings.length) {
        lastSavedCyclesRef.current = serializeCyclePayload([], ['moons'], false);
        setCycleRings(getDefaultCycleSet(dayOfYear, new Date()).cycles);
        setHiddenRings(['moons']);
        return;
      }

      // Ensure runtime fields are always numeric to avoid NaN in derived views (e.g., Galactic Time)
      // Also re-apply canonical displayOrder so ring order changes propagate to existing saved data.
      const safeRings = validRings.map((ring) => ({
        ...ring,
        rotation: Number.isFinite(ring.rotation) ? ring.rotation : 0,
        currentStage: Number.isFinite(ring.currentStage)
          ? ring.currentStage
          : 0,
        displayOrder: CANONICAL_RING_DISPLAY_ORDER[ring.id] ?? ring.displayOrder,
      }));

      setCycleRings(safeRings);
      const loadedHidden = data.hiddenRings ?? [];
      const forcedHidden = Array.from(new Set([...loadedHidden, 'moons']));
      setHiddenRings(forcedHidden);
      lastSavedCyclesRef.current = serializeCyclePayload(
        safeRings,
        forcedHidden,
        false,
      );
    };

    const unsubscribe = subscribeCycleData(cycleDocId, (data) => {
      applyCycleData(data);
      if (isActive) setCyclesLoaded(true);
    });

    if (!unsubscribe) {
      loadCycleData(cycleDocId)
        .then((data) => {
          applyCycleData(data);
        })
        .catch(() => undefined)
        .finally(() => {
          if (isActive) {
            setCyclesLoaded(true);
          }
        });
    }

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, [
    activeView,
    cycleDocId,
    dayOfYear,
    serializeCyclePayload,
    selectedGroupId,
  ]);

  const getStageFromRotation = useCallback(
    (rotation: number, stages: number) => {
      const segmentAngle = 360 / stages;
      const normalizedRotation = ((-rotation % 360) + 360) % 360;
      // Use floor to keep stage calculations aligned with render math
      return Math.floor(normalizedRotation / segmentAngle + 1e-6) % stages;
    },
    [],
  );

  const getDayOfYear = useCallback((day: Day13): number => {
    // Day of year is 1-indexed: Day 1 of Month 1 = 1, Drift = 365
    if (day.isOutOfTime) return 365;
    return (day.month - 1) * 28 + day.day;
  }, []);

  // Track whether the cycles widget sync toggle is linked
  const isCyclesLinkedRef = useRef(true);
  useEffect(() => {
    isCyclesLinkedRef.current =
      (widgetSyncModes["cycles"] ?? "linked") === "linked";
  }, [widgetSyncModes]);

  // Auto-unlink timeline widget when user manually changes time; re-link when NOW is pressed
  const timelineAutoLinkRef = useRef(false);
  useEffect(() => {
    const shouldUnlink =
      isManualMode && (widgetSyncModes["timeline"] ?? "linked") === "linked";
    const shouldRelink =
      !isManualMode &&
      (widgetSyncModes["timeline"] ?? "linked") === "isolated" &&
      timelineAutoLinkRef.current;

    if (shouldUnlink) {
      timelineAutoLinkRef.current = true;
      setWidgetSyncModes((prev) => ({ ...prev, timeline: "isolated" }));
    } else if (shouldRelink) {
      timelineAutoLinkRef.current = false;
      setWidgetSyncModes((prev) => ({ ...prev, timeline: "linked" }));
    }
  }, [isManualMode]);

  // Merge space weather cycles into dashboard cycles
  const { cycles: spaceWeatherCycles } = useSpaceWeatherCycles();
  useEffect(() => {
    if (!spaceWeatherCycles.length) return;
    
    setCycleRings((prev) => {
      // Check if space weather cycles are already in the list
      const hasSpaceWeatherCycles = prev.some((ring) =>
        ['geomagnetic-activity', 'solar-wind-pressure', 'electron-flux', 'alert-severity', 'imf-bz'].includes(ring.id)
      );
      
      if (hasSpaceWeatherCycles) {
        // Update existing space weather cycles with new data
        return prev.map((ring) => {
          const swRing = spaceWeatherCycles.find((sw) => sw.id === ring.id);
          return swRing ? { ...ring, ...swRing } : ring;
        });
      } else {
        // Add space weather cycles to the end
        return [...prev, ...spaceWeatherCycles];
      }
    });
  }, [spaceWeatherCycles]);

  const syncCyclesToDateTime = useCallback(
    (targetDate: Date) => {
      const hours = targetDate.getHours();
      const minutes = targetDate.getMinutes();
      const seconds = targetDate.getSeconds();
      const fractionOfDay = (hours * 3600 + minutes * 60 + seconds) / 86400;

      const targetDay13 = gregorianToDay13(targetDate, config);

      const isDrift = targetDay13.isOutOfTime;
      const dayInMonth = isDrift ? 0 : targetDay13.day;
      const monthNum = isDrift ? 13 : targetDay13.month;
      const dayOfYr = isDrift ? 365 : (monthNum - 1) * 28 + dayInMonth;

      // Keep rotations continuous across cycle boundaries by choosing the equivalent
      // angle (±360k) that is closest to the current rotation.
      const rotationNearestTo = (
        prevRotation: number,
        nextRotation: number,
      ) => {
        if (!Number.isFinite(prevRotation)) {
          return nextRotation;
        }
        const turns = Math.round((prevRotation - nextRotation) / 360);
        return nextRotation + turns * 360;
      };

      // Virtual timeline parent: represents the current Gregorian day fraction
      const getTimelineExactPosition = () => dayOfYr - 1 + fractionOfDay;

      // Update ring rotations based on timeline date
      setCycleRings((prevRings) => {
        // Always sync rings to timeline date, regardless of widget link state
        // (widget link state only affects multi-widget coordination, not timeline-to-cycles sync)

        const ringMap = new Map(prevRings.map((ring) => [ring.id, ring]));
        const timelineExact = getTimelineExactPosition();
        const linkedCache = new Map<string, boolean>();
        const positionCache = new Map<string, number>();

        const isTimelineLinked = (ring: CycleRing): boolean => {
          if (linkedCache.has(ring.id)) {
            return linkedCache.get(ring.id)!;
          }

          let linked = false;
          if (ring.id === "days" || ring.id === "moons") {
            linked = true;
          } else if (ring.parentId === "__timeline__") {
            linked = true;
          } else if (ring.parentId) {
            const parent = ringMap.get(ring.parentId);
            linked = parent ? isTimelineLinked(parent) : false;
          }

          linkedCache.set(ring.id, linked);
          return linked;
        };

        const resolveExactPosition = (ring: CycleRing): number | null => {
          if (positionCache.has(ring.id)) {
            return positionCache.get(ring.id)!;
          }

          let exactPosition: number | null = null;

          if (ring.id === "days") {
            exactPosition = isDrift
              ? 27.5 + fractionOfDay
              : dayInMonth - 1 + fractionOfDay;
          } else if (ring.id === "moons") {
            // moons: 13 stages, each stage = 1 moon = 28 days
            exactPosition = isDrift
              ? 12 + fractionOfDay
              : (monthNum - 1) + (dayInMonth - 1 + fractionOfDay) / 28;
          } else if (ring.parentId === "__timeline__" && ring.stagesPerParent) {
            exactPosition = timelineExact * ring.stagesPerParent;
          } else if (ring.parentId && ring.stagesPerParent) {
            const parent = ringMap.get(ring.parentId);
            if (parent && isTimelineLinked(parent)) {
              const parentPosition = resolveExactPosition(parent);
              if (parentPosition !== null) {
                exactPosition = parentPosition * ring.stagesPerParent;
              }
            }
          }

          if (exactPosition !== null) {
            positionCache.set(ring.id, exactPosition);
          }
          return exactPosition;
        };

        return prevRings.map((ring) => {
          if (!isTimelineLinked(ring)) {
            return ring;
          }

          const exactPosition = resolveExactPosition(ring);
          if (exactPosition === null) {
            return ring;
          }

          const stages = Math.max(1, ring.stages ?? 1);
          const segmentAngle = 360 / stages;
          const baseRotation = -exactPosition * segmentAngle;
          const rotation = rotationNearestTo(ring.rotation, baseRotation);
          const stage = Math.floor(exactPosition) % stages;

          return {
            ...ring,
            rotation,
            currentStage: stage,
          };
        });
      });
    },
    [config],
  );

  // Auto-sync cycles to current time when not in manual mode
  useEffect(() => {
    if (!isManualMode && autoSync && cycleRings.length > 0) {
      syncCyclesToDateTime(currentTime);
    }
  }, [
    currentTime,
    isManualMode,
    autoSync,
    cycleRings.length,
    syncCyclesToDateTime,
  ]);

  const clearWidgetEditModes = useCallback(() => {
    setWidgetEditModes((prev) => {
      if (Object.keys(prev).length === 0) {
        return prev;
      }
      return {};
    });
  }, []);

  const applyTimelineData = useCallback(
    (data: TimelineData | null) => {
      const mode = data?.mode === "manual" ? "manual" : "live";
      const now = new Date();

      const rememberedAnchor = lastMasterTimestampRef.current
        ? new Date(lastMasterTimestampRef.current)
        : null;
      const anchor = data?.timestamp
        ? new Date(data.timestamp)
        : displayTimeRef.current ?? rememberedAnchor ?? now;
      const validAnchor = Number.isNaN(anchor.getTime()) ? now : anchor;

      const incomingSerialized = serializeTimelineState({ mode, timestamp: data?.timestamp });
      const pendingTimeline = pendingTimelineRef.current;
      if (pendingTimeline) {
        const elapsed = Date.now() - lastTimelineLocalUpdateRef.current;
        if (incomingSerialized !== pendingTimeline && elapsed < 1500) {
          return;
        }
        if (incomingSerialized === pendingTimeline || elapsed >= 1500) {
          pendingTimelineRef.current = null;
        }
      }

      lastSavedTimelineRef.current = incomingSerialized;

      if (mode === "manual") {
        setAutoSync(true);
        setIsManualMode(true);
        setIsNowLocked(false);
        lastMasterTimestampRef.current = validAnchor.toISOString();
        setDisplayTime(validAnchor);
        setGregorianViewMonth(new Date(validAnchor));

        const galacticDay = gregorianToDay13(validAnchor, config);
        setSelectedYear(galacticDay.year);
        selectDay(galacticDay);
        if (galacticDay.isOutOfTime) {
          setSelectedMonth(14);
        } else {
          setSelectedMonth(galacticDay.month);
        }
        syncCyclesToDateTime(validAnchor);
        return;
      }

      setIsManualMode(false);
      setIsNowLocked(true);
      setAutoSync(true);
      clearWidgetEditModes();
      setDisplayTime(null);
      const galacticDay = gregorianToDay13(now, config);
      setCurrentTime(now);
      setGregorianViewMonth(new Date(now));
      setSelectedYear(galacticDay.year);
      selectDay(galacticDay);
      setSelectedMonth(galacticDay.isOutOfTime ? 14 : galacticDay.month);
    },
    [
      clearWidgetEditModes,
      config,
      selectDay,
      setSelectedMonth,
      setSelectedYear,
      setGregorianViewMonth,
      syncCyclesToDateTime,
    ],
  );

  const isWidgetSyncEnabled = useCallback(
    (_widgetId: string) => true,
    [],
  );

  const enterWidgetEditMode = useCallback((widgetId: string) => {
    clearWidgetEditModes();
    persistMasterSyncState(widgetId);
  }, [clearWidgetEditModes, persistMasterSyncState]);

  const setWidgetAsMaster = useCallback((widgetId?: string, anchorDate?: Date) => {
    clearWidgetEditModes();
    if (widgetId) {
      persistMasterSyncState(widgetId, anchorDate?.toISOString());
    }
  }, [clearWidgetEditModes, persistMasterSyncState]);

  const releaseWidgetMaster = useCallback(() => {
    // Intentionally keep the last focused widget as master until another widget claims focus.
  }, []);

  const isWidgetTimelineLinked = useCallback(
    (widgetId: string) =>
      isWidgetSyncEnabled(widgetId) && (!lastMasterWidgetId || lastMasterWidgetId === widgetId),
    [isWidgetSyncEnabled, lastMasterWidgetId],
  );

  useEffect(() => {
    if (!activeView || !timelineDocId) return;
    let isActive = true;
    setTimelineLoaded(false);
    lastSavedTimelineRef.current = null;

    const unsubscribe = subscribeTimelineData(timelineDocId, (data) => {
      if (!isActive) return;
      applyTimelineData(data);
      if (isActive) setTimelineLoaded(true);
    });

    if (!unsubscribe) {
      loadTimelineData(timelineDocId)
        .then((data) => {
          if (!isActive) return;
          applyTimelineData(data);
        })
        .catch(() => undefined)
        .finally(() => {
          if (isActive) setTimelineLoaded(true);
        });
    }

    return () => {
      isActive = false;
      unsubscribe?.();
    };
  }, [activeView, applyTimelineData, timelineDocId]);

  // Re-sync cycles when we're in manual mode and cycles finish loading.
  // applyTimelineData already calls syncCyclesToDateTime on every update,
  // so this only needs to fire when cyclesLoaded flips or mode changes.
  useEffect(() => {
    if (!isManualMode || !cyclesLoaded) return;
    const dt = displayTimeRef.current;
    if (!dt) return;
    syncCyclesToDateTime(dt);
  }, [cyclesLoaded, isManualMode, syncCyclesToDateTime]);

  useEffect(() => {
    if (!timelineDocId) return;
    const interval = window.setInterval(
      () => {
        loadTimelineData(timelineDocId)
          .then((data) => {
            applyTimelineData(data);
          })
          .catch(() => undefined);
      },
      60 * 60 * 1000,
    );

    return () => {
      window.clearInterval(interval);
    };
  }, [applyTimelineData, timelineDocId]);

  // Persist pending timeline only when the user explicitly sets one.
  // publishManualTimelineDate / publishLiveTimeline write directly via saveTimelineData.
  // This effect handles the edge case where timelineDocId wasn't available at write time.
  useEffect(() => {
    if (!timelineDocId || !timelineLoaded) return;
    const pending = pendingTimelineRef.current;
    if (!pending) return;

    let payload: TimelineData | null = null;
    try {
      payload = JSON.parse(pending) as TimelineData;
    } catch {
      payload = null;
    }

    if (!payload) return;

    const serialized = serializeTimelineState(payload);
    if (serialized === lastSavedTimelineRef.current) return;

    lastSavedTimelineRef.current = serialized;
    timelineDirtyRef.current = false;
    pendingTimelineRef.current = null;
    void saveTimelineData(timelineDocId, payload).catch(
      () => undefined,
    );
  }, [timelineDocId, timelineLoaded, pendingTimelineVersion]);

  const publishManualTimelineDate = useCallback(
    (date: Date, options?: { force?: boolean }) => {
      if (isNowLocked && !options?.force) {
        toast("Timeline is locked — tap unlock first.");
        return false;
      }

      const payload: TimelineData = {
        mode: "manual",
        timestamp: date.toISOString(),
      };

      // Block the Firestore listener from overwriting our optimistic update
      // for 1500ms (same window used by applyTimelineData's guard).
      pendingTimelineRef.current = serializeTimelineState(payload);
      lastTimelineLocalUpdateRef.current = Date.now();
      timelineDirtyRef.current = true;

      // Update display state immediately so header/waveform react in real time.
      // Sync rings to this date so all UI elements stay in lockstep.
      setDisplayTime(date);
      setIsManualMode(true);
      setIsNowLocked(false);
      lastMasterTimestampRef.current = date.toISOString();
      // Also update selectedDay so dayOfYear/Mayan header date tracks in real time.
      selectDay(gregorianToDay13(date, config));
      syncCyclesToDateTime(date);

      if (timelineDocId) {
        // Debounce the Firestore write — during drag this fires every frame,
        // we only want to persist once the user settles (400ms after last event).
        pendingFirestorePayloadRef.current = payload;
        if (firestoreWriteTimerRef.current) clearTimeout(firestoreWriteTimerRef.current);
        firestoreWriteTimerRef.current = setTimeout(() => {
          firestoreWriteTimerRef.current = null;
          const latestPayload = pendingFirestorePayloadRef.current;
          if (!latestPayload) return;
          pendingFirestorePayloadRef.current = null;
          lastSavedTimelineRef.current = serializeTimelineState(latestPayload);
          pendingTimelineRef.current = null;
          timelineDirtyRef.current = false;
          allowTimelineSaveOnce(timelineDocId);
          void saveTimelineData(timelineDocId, latestPayload).catch(() => undefined);
        }, 400);
        return true;
      }
      setPendingTimeline(payload);
      return true;
    },
    [isNowLocked, setDisplayTime, setIsManualMode, selectDay, config, setPendingTimeline, timelineDocId, syncCyclesToDateTime],
  );

  const publishLiveTimeline = useCallback(() => {
    const payload: TimelineData = { mode: "live" };
    if (timelineDocId) {
      lastSavedTimelineRef.current = serializeTimelineState(payload);
      pendingTimelineRef.current = null;
      timelineDirtyRef.current = false;
      allowTimelineSaveOnce(timelineDocId);
      void saveTimelineData(timelineDocId, payload).catch(() => undefined);
      return;
    }
    applyTimelineData(payload);
  }, [applyTimelineData, timelineDocId]);

  const handleSelectDay = useCallback(
    (day: Day13) => {
      const gregorianDate = day13ToGregorian(
        day.year,
        day.month,
        day.day,
        config,
      );
      const newDisplayTime = new Date(gregorianDate);
      newDisplayTime.setHours(
        currentTime.getHours(),
        currentTime.getMinutes(),
        currentTime.getSeconds(),
      );
      publishManualTimelineDate(newDisplayTime);
    },
    [
      config,
      currentTime,
      publishManualTimelineDate,
    ],
  );

  const handleSelectDayFromGrid = useCallback(
    (day: Day13) => {
      if (!isWidgetSyncEnabled("grid")) return;
      setWidgetAsMaster("grid");
      handleSelectDay(day);
    },
    [handleSelectDay, isWidgetSyncEnabled, setWidgetAsMaster],
  );

  // Handle selecting a date from Gregorian grid
  const handleSelectGregorianDate = useCallback(
    (date: Date) => {
      const newDisplayTime = new Date(date);
      newDisplayTime.setHours(
        currentTime.getHours(),
        currentTime.getMinutes(),
        currentTime.getSeconds(),
      );
      publishManualTimelineDate(newDisplayTime);
    },
    [
      currentTime,
      publishManualTimelineDate,
    ],
  );

  const applyManualTimelineDate = useCallback(
    (date: Date) => {
      const nextDate = new Date(date);
      publishManualTimelineDate(nextDate);
    },
    [publishManualTimelineDate],
  );

  const handleSelectGregorianDateFromGrid = useCallback(
    (date: Date) => {
      if (!isWidgetSyncEnabled("gregorian-grid")) return;
      setWidgetAsMaster("gregorian-grid", date);
      handleSelectGregorianDate(date);
    },
    [handleSelectGregorianDate, isWidgetSyncEnabled, setWidgetAsMaster],
  );

  const handleGridPrevMonth = useCallback(() => {
    enterWidgetEditMode("grid");
    const monthLength = selectedMonth === 14 ? 1 : 28;
    const baseDate = displayTime ?? currentTime;
    const nextDate = new Date(baseDate);
    nextDate.setDate(nextDate.getDate() - monthLength);
    // Optimistic: update state immediately so the grid responds without waiting for the emit chain
    const galacticDay = gregorianToDay13(nextDate, config);
    setDisplayTime(nextDate);
    setIsManualMode(true);
    setSelectedYear(galacticDay.year);
    setSelectedMonth(galacticDay.isOutOfTime ? 14 : galacticDay.month);
    applyManualTimelineDate(nextDate);
  }, [
    applyManualTimelineDate,
    config,
    currentTime,
    displayTime,
    enterWidgetEditMode,
    selectedMonth,
  ]);

  const handleGridNextMonth = useCallback(() => {
    enterWidgetEditMode("grid");
    const monthLength = selectedMonth === 14 ? 1 : 28;
    const baseDate = displayTime ?? currentTime;
    const nextDate = new Date(baseDate);
    nextDate.setDate(nextDate.getDate() + monthLength);
    const galacticDay = gregorianToDay13(nextDate, config);
    setDisplayTime(nextDate);
    setIsManualMode(true);
    setSelectedYear(galacticDay.year);
    setSelectedMonth(galacticDay.isOutOfTime ? 14 : galacticDay.month);
    applyManualTimelineDate(nextDate);
  }, [
    applyManualTimelineDate,
    config,
    currentTime,
    displayTime,
    enterWidgetEditMode,
    selectedMonth,
  ]);

  const handleGregorianPrevMonth = useCallback(() => {
    enterWidgetEditMode("gregorian-grid");
    const baseDate = displayTime ?? currentTime;
    const nextDate = subMonths(baseDate, 1);
    setDisplayTime(nextDate);
    setIsManualMode(true);
    setGregorianViewMonth(new Date(nextDate));
    applyManualTimelineDate(nextDate);
  }, [applyManualTimelineDate, currentTime, displayTime, enterWidgetEditMode]);

  const handleGregorianNextMonth = useCallback(() => {
    enterWidgetEditMode("gregorian-grid");
    const baseDate = displayTime ?? currentTime;
    const nextDate = addMonths(baseDate, 1);
    setDisplayTime(nextDate);
    setIsManualMode(true);
    setGregorianViewMonth(new Date(nextDate));
    applyManualTimelineDate(nextDate);
  }, [applyManualTimelineDate, currentTime, displayTime, enterWidgetEditMode]);

  const handleYearRingSelectMonth = useCallback(
    (month: number) => {
      enterWidgetEditMode("year-ring");
      const baseDate = displayTime ?? currentTime;
      const baseGalacticDay = gregorianToDay13(baseDate, config);
      const nextDay = baseGalacticDay.isOutOfTime ? 1 : Math.max(1, Math.min(28, baseGalacticDay.day));
      const nextDate = day13ToGregorian(selectedYear, month, nextDay, config);
      nextDate.setHours(
        baseDate.getHours(),
        baseDate.getMinutes(),
        baseDate.getSeconds(),
        baseDate.getMilliseconds(),
      );
      publishManualTimelineDate(nextDate);
    },
    [config, currentTime, displayTime, enterWidgetEditMode, publishManualTimelineDate, selectedYear],
  );

  const handleSyncToNow = useCallback(() => {
    if (isNowLocked) {
      const unlockedAt = new Date();
      publishManualTimelineDate(unlockedAt, { force: true });
      toast.success("Timeline unlocked");
      return;
    }

    const now = new Date();
    if (lastMasterWidgetId) {
      persistMasterSyncState(lastMasterWidgetId, now.toISOString());
    }
    setIsNowLocked(true);
    publishLiveTimeline();
    toast.success("Locked to current time");
  }, [
    isNowLocked,
    lastMasterWidgetId,
    persistMasterSyncState,
    publishManualTimelineDate,
    publishLiveTimeline,
  ]);

  const handleSyncToNowFromGrid = useCallback(() => {
    // NOW button should always work, regardless of widget sync state
    handleSyncToNow();
  }, [handleSyncToNow]);

  const handleSyncToNowFromTimeline = useCallback(() => {
    // NOW button should always work, regardless of widget sync state
    handleSyncToNow();
  }, [handleSyncToNow]);

  const handleSyncToNowFromCycles = useCallback(() => {
    if (!isWidgetSyncEnabled("cycles")) return;
    setWidgetAsMaster("cycles");
    handleSyncToNow();
  }, [handleSyncToNow, isWidgetSyncEnabled, setWidgetAsMaster]);

  const handleSyncToDrift = useCallback(() => {
    setSelectedMonth(14);
    const nextYearStart = getYearStartDate(selectedYear + 1, config);
    const driftDate = new Date(nextYearStart);
    driftDate.setDate(driftDate.getDate() - 1);
    const driftDay: Day13 = {
      year: selectedYear,
      month: 0,
      day: 0,
      isOutOfTime: true,
      gregorianDate: driftDate,
      weekday: -1,
    };
    selectDay(driftDay);
    setIsManualMode(true);

    driftDate.setHours(12, 0, 0, 0);
    publishManualTimelineDate(driftDate);

    toast.info("Viewing: Day Out of Time ✨");
  }, [
    selectedYear,
    config,
    publishManualTimelineDate,
  ]);

  const handleSyncToDriftFromGrid = useCallback(() => {
    if (!isWidgetSyncEnabled("grid")) return;
    setWidgetAsMaster("grid");
    handleSyncToDrift();
  }, [handleSyncToDrift, isWidgetSyncEnabled, setWidgetAsMaster]);

  const handleRotationChange = useCallback(
    (newDayOfYear: number, fractionOfDay: number) => {
      if (!Number.isFinite(newDayOfYear)) return;
      const clampedDay = Math.max(1, Math.min(365, newDayOfYear));
      if (!Number.isFinite(clampedDay)) return;
      const wholeDay = Math.floor(clampedDay);
      const month = Math.min(13, Math.floor((wholeDay - 1) / 28) + 1);
      const dayInMonth = ((wholeDay - 1) % 28) + 1;

      const isDrift = wholeDay === 365;
      const gregorianDate = day13ToGregorian(
        selectedYear,
        isDrift ? 0 : month,
        isDrift ? 0 : dayInMonth,
        config,
      );

      // Set time from fraction
      const safeFraction = Number.isFinite(fractionOfDay)
        ? Math.max(0, Math.min(1, fractionOfDay))
        : 0;
      const normalizedFraction = Math.min(0.999999, safeFraction);
      const totalSeconds = normalizedFraction * 86400;
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = Math.floor(totalSeconds % 60);
      const milliseconds = Math.floor((totalSeconds - Math.floor(totalSeconds)) * 1000);
      gregorianDate.setHours(hours, minutes, seconds, milliseconds);
      setWidgetAsMaster("timeline", gregorianDate);
      publishManualTimelineDate(gregorianDate);
    },
    [
      publishManualTimelineDate,
      selectedYear,
      config,
      setWidgetAsMaster,
    ],
  );

  const handleRotationChangeFromCycles = useCallback(
    (newDayOfYear: number, fractionOfDay: number) => {
      if (!Number.isFinite(newDayOfYear)) return;
      // Manual ring rotation should always drive the shared timeline.
      const clampedDay = Math.max(1, Math.min(365, newDayOfYear));
      if (!Number.isFinite(clampedDay)) return;
      const wholeDay = Math.floor(clampedDay);
      const month = Math.min(13, Math.floor((wholeDay - 1) / 28) + 1);
      const dayInMonth = ((wholeDay - 1) % 28) + 1;

      const isDrift = wholeDay === 365;
      const gregorianDate = day13ToGregorian(
        selectedYear,
        isDrift ? 0 : month,
        isDrift ? 0 : dayInMonth,
        config,
      );

      // Set time from fraction
      const safeFraction = Number.isFinite(fractionOfDay)
        ? Math.max(0, Math.min(1, fractionOfDay))
        : 0;
      const normalizedFraction = Math.min(0.999999, safeFraction);
      const totalSeconds = normalizedFraction * 86400;
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = Math.floor(totalSeconds % 60);
      const milliseconds = Math.floor((totalSeconds - Math.floor(totalSeconds)) * 1000);
      gregorianDate.setHours(hours, minutes, seconds, milliseconds);

      setWidgetAsMaster("cycles", gregorianDate);
      publishManualTimelineDate(gregorianDate);
    },
    [
      publishManualTimelineDate,
      setWidgetAsMaster,
      config,
      selectedYear,
    ],
  );

  const handleWaveformChange = useCallback(
    (newDayOfYear: number, fractionOfDay: number) => {
      if (!Number.isFinite(newDayOfYear)) return;
      const maxDay = Math.max(
        365,
        ...cycleRings.map((r) => Math.max(1, r.stages || 0)),
      );
      const clampedDay = Math.max(1, Math.min(maxDay, newDayOfYear));
      if (!Number.isFinite(clampedDay)) return;
      const month = Math.min(13, Math.floor((clampedDay - 1) / 28) + 1);
      const dayInMonth = ((clampedDay - 1) % 28) + 1;

      const isDrift = clampedDay === 365;
      const gregorianDate = day13ToGregorian(
        selectedYear,
        isDrift ? 0 : month,
        isDrift ? 0 : dayInMonth,
        config,
      );

      const safeFraction = Number.isFinite(fractionOfDay)
        ? Math.max(0, Math.min(1, fractionOfDay))
        : 0;
      const hours = Math.floor(safeFraction * 24);
      const minutes = Math.floor((safeFraction * 24 - hours) * 60);
      gregorianDate.setHours(hours, minutes, 0, 0);
      publishManualTimelineDate(gregorianDate);
    },
    [
      cycleRings,
      publishManualTimelineDate,
      selectedYear,
      config,
    ],
  );

  const handleWaveformChangeFromWidget = useCallback(
    (newDayOfYear: number, fractionOfDay: number) => {
      if (!isWidgetSyncEnabled("waveform")) {
        return;
      }
      setWidgetAsMaster("waveform");
      handleWaveformChange(newDayOfYear, fractionOfDay);
    },
    [handleWaveformChange, isWidgetSyncEnabled, setWidgetAsMaster],
  );

  const handleWaveformInteractionStart = useCallback(() => {
    setWidgetAsMaster("waveform");
  }, [setWidgetAsMaster]);

  const bumpVisibleRingCount = useCallback(
    (nextRingsLength: number) => {
      const activeThemeVisibleCount = ringThemeState.themes.find(
        (theme) => theme.id === ringThemeState.activeThemeId,
      )?.visibleRingCount;
      setWidgetVisibleCount((prev) => {
        const baseVisible =
          typeof prev === "number"
            ? prev
            : typeof activeThemeVisibleCount === "number"
              ? activeThemeVisibleCount
              : Math.min(maxWidgetRingsWithCycles, nextRingsLength - 1);
        const maxVisible = Math.min(maxWidgetRingsWithCycles, nextRingsLength);
        return Math.min(maxVisible, baseVisible + 1);
      });
    },
    [maxWidgetRingsWithCycles, ringThemeState],
  );

  useEffect(() => {
    setWidgetVisibleCount((prev) => {
      if (typeof prev !== "number") return prev;
      return Math.min(maxWidgetRingsWithCycles, prev);
    });
  }, [maxWidgetRingsWithCycles]);

  const effectiveVisibleRingCount = useMemo(() => {
    const activeThemeVisibleCount = ringThemeState.themes.find(
      (theme) => theme.id === ringThemeState.activeThemeId,
    )?.visibleRingCount;
    const baseVisible =
      typeof widgetVisibleCount === "number"
        ? widgetVisibleCount
        : typeof activeThemeVisibleCount === "number"
          ? activeThemeVisibleCount
          : cycleRings.length;
    return Math.max(1, Math.min(baseVisible, cycleRings.length || 1));
  }, [cycleRings.length, ringThemeState.activeThemeId, ringThemeState.themes, widgetVisibleCount]);

  const {
    editingCycle,
    setEditingCycle,
    handleAddCycle,
    handleUpdateCycle,
    handleRemoveCycle,
    handleEditCycle,
    handleAddAlignment,
    handleApplyAlignment,
    handleToggleRing,
    handleReorderRings,
    handleSetSkinMode,
    handleRingsChange,
    handleAddRingFromPreset,
    liveGalacticDay,
    indicatorDay,
    shouldUseLiveDay,
    displayDay,
    waveformDayOfYear,
    currentGregorianDate,
    activeRingTheme,
    cycleDisplayMode: cycleDisplayModeRaw,
    handleSaveRingTheme,
    handleDeleteRingTheme,
    handleApplyRingThemeLayout,
    handleApplyRingThemeToRing,
    handleApplyRingThemeOrder,
    handleCycleDisplayModeChange,
  } = useDashboardCycles({
    isAdmin,
    isManualMode,
    config,
    selectedYear,
    selectedMonth,
    setSelectedMonth,
    selectedDay,
    selectDay,
    today,
    shownTime,
    dayOfYear,
    displayTime,
    currentTime,
    cycleRings,
    setCycleRings,
    hiddenRingsRef,
    setHiddenRings,
    effectiveVisibleRingCount,
    bumpVisibleRingCount,
    saveCyclesNow,
    syncCyclesToDateTime,
    setShowCycleWizard,
    setAlignments,
    ringThemeState,
    updateRingThemeState,
    getStageFromRotation,
    getDayOfYear,
  });

  const cycleDisplayMode: 'single' | 'multi' =
    cycleDisplayModeRaw === 'single' ? 'single' : 'multi';

  // Auto-save relationships (parent links, ratios, and waveform selections) whenever they change.
  useEffect(() => {
    if (!relationshipDocId) return;

    if (relationshipAutosaveTimerRef.current) {
      clearTimeout(relationshipAutosaveTimerRef.current);
    }

    const ringRelationships = cycleRings.map((ring) => ({
      id: ring.id,
      parentId: ring.parentId ?? null,
      stagesPerParent: ring.stagesPerParent ?? null,
      waveform: ring.waveform,
      waveformIntervalMultiplier: ring.waveformIntervalMultiplier ?? null,
      waveformIntervalDivider: ring.waveformIntervalDivider ?? null,
    }));

    const payload = {
      alignments,
      ringRelationships,
    };
    const serializedPayload = JSON.stringify(payload);

    if (lastRelationshipAutosavePayloadRef.current === serializedPayload) {
      return;
    }
    lastRelationshipAutosavePayloadRef.current = serializedPayload;

    relationshipAutosaveTimerRef.current = setTimeout(() => {
      saveRelationshipData(relationshipDocId, payload, isAdmin).catch((error) => {
        try {
          console.error('[relationshipStore] autosave failed', error);
        } catch {
          // ignore logging errors
        }
      });
    }, 250);

    return () => {
      if (relationshipAutosaveTimerRef.current) {
        clearTimeout(relationshipAutosaveTimerRef.current);
        relationshipAutosaveTimerRef.current = null;
      }
    };
  }, [alignments, cycleRings, relationshipDocId, isAdmin]);

  // Helper: is a widget visible by id?
  const isWidgetVisible = (id: string) => visibleWidgets.includes(id);
  const { allWidgets, activeWidgets, addableWidgets } = useDashboardWidgets({
    currentGregorianDate,
    isManualMode,
    shownTime,
    displayTime,
    currentTime,
    displayDay,
    liveGalacticDay,
    shouldUseLiveDay,
    selectedDay,
    selectedMonth,
    selectedYear,
    currentMonth,
    allMonths,
    today,
    indicatorDay,
    gregorianViewMonth,
    waveformDayOfYear,
    viewId,
    config,
    updateConfig,
    cycleDocId,
    cycleRings: coreCycleRings,
    hiddenRings: effectiveHiddenRings,
    alignments,
    ringThemeState,
    activeRingTheme,
    cycleDisplayMode,
    widgetVisibleCount,
    setWidgetVisibleCount,
    setShowCycleWizard,
    setShowRingThemeWizard,
    setShowAlignmentTool,
    setAlignmentInitialCycleId,
    selectedGroupId,
    isAdmin,
    maxWidgetRings: maxWidgetRingsWithCycles,
    widgetSyncModes,
    widgetCustomizations,
    isolatedWidgetValues,
    visibleWidgets,
    releaseWidgetMaster,
    nextMonth,
    prevMonth,
    handleSyncToNowFromGrid,
    handleSyncToNowFromTimeline,
    handleSyncToNowFromCycles,
    handleSyncToDriftFromGrid,
    handleSyncToDrift,
    handleGridPrevMonth,
    handleGridNextMonth,
    handleSelectDayFromGrid,
    handleSelectGregorianDateFromGrid,
    handleGregorianPrevMonth,
    handleGregorianNextMonth,
    handleRemoveCycle,
    handleEditCycle,
    handleRotationChangeFromCycles,
    handleToggleRing,
    handleAddRingFromPreset,
    handleRingsChange,
    handleReorderRings,
    handleSetSkinMode,
    handleWaveformChangeFromWidget,
    handleWaveformInteractionStart,
    handleYearRingSelectMonth,
    isYearRingSynced,
    onToggleYearRingSync: handleToggleYearRingSync,
    onCycleDisplayModeChange: handleCycleDisplayModeChange,
  });

  useEffect(() => {
    // Removed auto-seeding of default widgets to allow widget removals to persist
    // This effect is now a no-op
  }, [
    activeView,
    allWidgets,
    areWidgetIdsEqual,
    updateActiveViewWidgets,
    visibleWidgets,
  ]);

  // Update isolated widget value cache when waveform is linked
  useEffect(() => {
    const isWaveformLinked =
      (widgetSyncModes["waveform"] ?? "linked") === "linked";
    if (isWaveformLinked) {
      setIsolatedWidgetValues((prev) => ({
        ...prev,
        waveform: waveformDayOfYear,
      }));
    }
  }, [waveformDayOfYear, widgetSyncModes]);

  const handleRemoveWidget = useCallback(
    async (id: string) => {
      const nextWidgets = visibleWidgets.filter((wid) => wid !== id);
      // Update Firebase first before changing local state
      await updateActiveViewWidgets(nextWidgets);
      // Local state will sync from the subscription
    },
    [visibleWidgets, updateActiveViewWidgets],
  );

  const handleSaveDashboard = useCallback(async () => {
    if (!canEditDashboard) return;
    // Layout and customizations auto-save from Dashboard; just persist widget list
    await updateActiveViewWidgets(visibleWidgets);
    toast.success("Dashboard saved");
  }, [
    updateActiveViewWidgets,
    visibleWidgets,
  ]);

  const handleAddWidget = useCallback(
    async (widgetId: string) => {
      console.log('[handleAddWidget] Called with widgetId:', widgetId, 'isAdmin:', isAdmin);
      if (!canEditDashboard) return;
      const hiddenWidgets = allWidgets.filter(
        (w) => !visibleWidgets.includes(w.id),
      );
      const nextWidget = hiddenWidgets.find((w) => w.id === widgetId);
      if (!nextWidget) {
        toast({
          title: "Cannot add widget",
          description: "All widgets are already visible",
        });
        return;
      }
      const nextWidgets = [...visibleWidgets, nextWidget.id];

      try {
        const currentViews = dashboardViewsRef.current;
        const currentActiveViewId = activeViewIdRef.current || currentViews[0]?.id || '';

        if (!currentActiveViewId) {
          throw new Error('No active dashboard view selected');
        }

        const activeIndex = currentViews.findIndex((view) => view.id === currentActiveViewId);
        if (activeIndex === -1) {
          throw new Error(
            `Active dashboard view "${currentActiveViewId}" not found in views: ${JSON.stringify(currentViews.map((v) => v.id))}`,
          );
        }

        const nextViews = currentViews.map((view, idx) =>
          idx === activeIndex ? { ...view, widgetIds: nextWidgets } : view,
        );

        console.log('[handleAddWidget] Persisting directly. viewCount:', nextViews.length, 'widgetIds:', nextViews[activeIndex].widgetIds);

        // Bypass the serialized queue — write directly so we can observe any failure clearly.
        // All users write to shared state; non-Paul writes fail via Firestore permission rules.
        await saveDashboardMeta({ views: nextViews, activeViewId: currentActiveViewId });

        console.log('[handleAddWidget] Firestore save completed');
        lastOptimisticUpdateRef.current = { timestamp: Date.now(), views: nextViews };
        setDashboardViews(nextViews);
        dashboardViewsRef.current = nextViews;

        setVisibleWidgets(nextWidgets);
        console.log('[handleAddWidget] Local state updated optimistically');

        toast({
          title: "Widget added",
          description: `Added ${nextWidget.title}`,
        });
      } catch (error) {
        console.error("[handleAddWidget] Failed to add widget:", error);
        toast({
          title: "Failed to add widget",
          description: "Could not add widget to dashboard",
          variant: "destructive",
        });
      }
    },
    [allWidgets, canEditDashboard, isAdmin, toast, visibleWidgets],
  );

  const navItems = useMemo(() => {
    if (!canEditDashboard) return [];
    return [
      { id: "dashboard", label: "Dashboard", icon: null, to: "/admin" },
      { id: "plans", label: "Plans", icon: null, to: "/plans" },
      { id: "roles", label: "User roles", icon: null, to: "/roles" },
    ];
  }, [canEditDashboard]);

  const dashboardStorageKey = useMemo(() => {
    if (!activeView) return undefined;
    return resolveDashboardLayoutStorageKey(
      `galactic-dashboard-layout-${activeView.id}`,
      dashboardBreakpoint,
    );
  }, [activeView, dashboardBreakpoint]);

  const handleImportCycles = useCallback((imported: CycleRing[]) => {
    setCycleRings((prev) => [...prev, ...imported]);
  }, []);

  const handleCloseCycleWizard = useCallback(() => {
    setShowCycleWizard(false);
    setEditingCycle(null);
  }, [setEditingCycle]);

  // Patch: inject isVisible to SpaceWeatherWidgetCard
  const patchedActiveWidgets = activeWidgets.map(w => {
    if (w.id === 'space-weather') {
      return {
        ...w,
        component: <SpaceWeatherWidgetCard timelineDate={displayTime ?? currentTime} isVisible={isWidgetVisible('space-weather')} />,
      };
    }
    return w;
  });

  return (
    <DashboardPageView
      isLoggedIn={isLoggedIn}
      canEditDashboard={canEditDashboard}
      requiresBetaAccess={requiresBetaAccess}
      metaLoaded={metaLoaded}
      dashboardStateReady={metaLoaded}
      betaEnabled={betaEnabled}
      betaDashboardsVisible={betaDashboardsVisible}
      isMobileBreakpoint={dashboardBreakpoint === "mobile"}
      navItems={navItems}
      showCycleWizard={showCycleWizard}
      setShowCycleWizard={setShowCycleWizard}
      showAlignmentTool={showAlignmentTool}
      setShowAlignmentTool={setShowAlignmentTool}
      showRingThemeWizard={showRingThemeWizard}
      setShowRingThemeWizard={setShowRingThemeWizard}
      alignmentInitialCycleId={alignmentInitialCycleId}
      setAlignmentInitialCycleId={setAlignmentInitialCycleId}
      currentGregorianDate={currentGregorianDate}
      isManualMode={isManualMode}
      isNowLocked={isNowLocked}
      handleSyncToNow={handleSyncToNow}
      dayOfYear={dayOfYear}
      dashboardViews={safeDashboardViews}
      activeViewId={activeViewId}
      setActiveViewId={handleSelectView}
      activeView={activeView}
      handleCreateView={handleCreateView}
      handleDeleteView={handleDeleteView}
      activeWidgets={patchedActiveWidgets}
      addableWidgets={addableWidgets}
      handleRemoveWidget={handleRemoveWidget}
      handleAddWidget={handleAddWidget}
      handleSaveDashboard={handleSaveDashboard}
      dashboardStorageKey={dashboardStorageKey ?? "dashboard-layout"}
      cycles={coreCycleRings}
      hiddenRings={effectiveHiddenRings}
      onToggleRing={handleToggleRing}
      onRingsChange={handleRingsChange}
      onRotationChangeFromCycles={handleRotationChangeFromCycles}
      alignments={alignments}
      ringThemeState={ringThemeState}
      onSaveTheme={handleSaveRingTheme}
      onDeleteTheme={handleDeleteRingTheme}
      onApplyThemeToRing={handleApplyRingThemeToRing}
      onApplyThemeLayout={handleApplyRingThemeLayout}
      onApplyThemeOrder={handleApplyRingThemeOrder}
      cycleDisplayMode={cycleDisplayMode}
      onCycleDisplayModeChange={handleCycleDisplayModeChange}
      onAlign={handleAddAlignment}
      onApplyAlignment={handleApplyAlignment}
      onImportCycles={handleImportCycles}
      onCreateCycle={handleAddCycle}
      onUpdateCycle={handleUpdateCycle}
      onDeleteCycle={handleRemoveCycle}
      editingCycle={editingCycle}
      onCloseCycleWizard={handleCloseCycleWizard}
      firestoreDisabled={firestoreDisabled}
      widgetSyncState={widgetSyncState}
      setWidgetSyncState={setWidgetSyncState}
      onSyncModesChange={(modes: Record<string, WidgetSyncMode>) => setWidgetSyncModes(modes)}
      onSignOut={handleSignOut}
    />
  );
};

export default DashboardPage;
